/**
 * Cart Page Tax Exempt Dropdown
 * Handles conditional display of exemption reason dropdown
 */

(function() {
  'use strict';

  const TAX_EXEMPT_STORAGE_KEY = 'bentley_tax_exempt_v2';
  const EXEMPTION_REASON_STORAGE_KEY = 'bentley_exemption_reason';
  const VAT_STORAGE_KEY = 'bentley_vat_setting_v2';

	  const VAT_EXEMPT_MAPPING_ELEMENT_ID = 'vatExemptProductMapping';
	  const VAT_EXEMPT_SUFFIX = '-vat-exempt';

	  let internalVatChange = false;

	  function getVatExemptMapping() {
	    const el = document.getElementById(VAT_EXEMPT_MAPPING_ELEMENT_ID);
	    if (!el) {
	      return {};
	    }
	    try {
	      return JSON.parse(el.textContent || el.innerText || '{}');
	    } catch (e) {
	      // eslint-disable-next-line no-console
	      console.error('Invalid VAT exempt product mapping JSON', e);
	      return {};
	    }
	  }

	  function hasVatExemptSuffix(handle) {
	    if (!handle || typeof handle !== 'string') return false;
	    return handle.slice(-VAT_EXEMPT_SUFFIX.length) === VAT_EXEMPT_SUFFIX;
	  }

	  function getCartItemOptions(cartItem) {
	    if (!cartItem) return [];
	    if (Array.isArray(cartItem.variant_options)) {
	      return cartItem.variant_options;
	    }
	    if (Array.isArray(cartItem.options_with_values)) {
	      return cartItem.options_with_values
	        .map(function(opt) { return opt && opt.value; })
	        .filter(Boolean);
	    }
	    return [];
	  }

	  function getVariantOptionsArray(variant) {
	    if (!variant) return [];
	    if (Array.isArray(variant.options) && variant.options.length) {
	      return variant.options;
	    }
	    const result = [];
	    if (variant.option1) result.push(variant.option1);
	    if (variant.option2) result.push(variant.option2);
	    if (variant.option3) result.push(variant.option3);
	    return result;
	  }

	  function findMatchingVariantByOptions(product, cartItem) {
	    if (!product || !Array.isArray(product.variants)) {
	      return null;
	    }
	    const cartOptions = getCartItemOptions(cartItem);
	    return product.variants.find(function(variant) {
	      const variantOptions = getVariantOptionsArray(variant);
	      if (variantOptions.length !== cartOptions.length) {
	        return false;
	      }
	      for (var i = 0; i < variantOptions.length; i++) {
	        if (variantOptions[i] !== cartOptions[i]) {
	          return false;
	        }
	      }
	      return true;
	    }) || null;
	  }

	  const productJsonCache = {};

	  function fetchProductByHandle(handle) {
	    if (!handle || typeof fetch === 'undefined') {
	      return Promise.resolve(null);
	    }
	    if (productJsonCache[handle]) {
	      return Promise.resolve(productJsonCache[handle]);
	    }
	    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
	      credentials: 'same-origin'
	    })
	      .then(function(response) {
	        if (!response.ok) {
	          return null;
	        }
	        return response.json();
	      })
	      .then(function(json) {
	        if (json) {
	          productJsonCache[handle] = json;
	        }
	        return json;
	      })
	      .catch(function() {
	        return null;
	      });
	  }

	  function swapCartToVatExempt(enableExempt, reason) {
	    if (typeof fetch === 'undefined') {
	      return Promise.resolve();
	    }

	    const vatMapping = getVatExemptMapping();

	    return fetch('/cart.js', {
	      credentials: 'same-origin',
	      cache: 'no-cache'
	    })
	      .then(function(response) { return response.json(); })
	      .then(function(cart) {
	        if (!cart || !Array.isArray(cart.items)) {
	          return;
	        }

	        const updates = {};
	        const productPromises = [];

	        cart.items.forEach(function(item) {
	          if (enableExempt) {
	            const exemptHandle = vatMapping[item.handle];
	            if (!exemptHandle || item.handle === exemptHandle || hasVatExemptSuffix(item.handle)) {
	              return;
	            }

	            productPromises.push(
	              fetchProductByHandle(exemptHandle).then(function(exemptProduct) {
	                if (!exemptProduct) return;
	                const matchingVariant = findMatchingVariantByOptions(exemptProduct, item);
	                if (!matchingVariant) return;

	                updates[item.id] = 0;
	                if (updates[matchingVariant.id]) {
	                  updates[matchingVariant.id] += item.quantity;
	                } else {
	                  updates[matchingVariant.id] = item.quantity;
	                }
	              })
	            );
	          } else {
	            if (!hasVatExemptSuffix(item.handle)) {
	              return;
	            }

	            const normalHandle = item.handle.replace(/-vat-exempt$/, '');
	            productPromises.push(
	              fetchProductByHandle(normalHandle).then(function(normalProduct) {
	                if (!normalProduct) return;
	                const normalVariant = findMatchingVariantByOptions(normalProduct, item);
	                if (!normalVariant) return;

	                updates[item.id] = 0;
	                if (updates[normalVariant.id]) {
	                  updates[normalVariant.id] += item.quantity;
	                } else {
	                  updates[normalVariant.id] = item.quantity;
	                }
	              })
	            );
	          }
	        });

	        return Promise.all(productPromises).then(function() {
	          const attributes = {
	            vat_exempt: enableExempt ? 'yes' : 'no'
	          };

	          // Only include reason if exempt and reason is provided
	          if (enableExempt && reason && reason.trim()) {
	            attributes.vat_exempt_reason = reason.trim();
	          } else {
	            // Clear the reason attribute when not exempt
	            attributes.vat_exempt_reason = '';
	          }

	          const payload = {
	            attributes: attributes
	          };

	          if (Object.keys(updates).length > 0) {
	            payload.updates = updates;
	          }

	          return fetch('/cart/update.js', {
	            method: 'POST',
	            credentials: 'same-origin',
	            headers: {
	              'Content-Type': 'application/json'
	            },
	            body: JSON.stringify(payload)
	          })
	            .then(function(response) {
	              if (!response.ok) {
	                // eslint-disable-next-line no-console
	                console.error('Failed to update cart for tax exemption');
	                return null;
	              }
	              return response.json().catch(function() { return null; });
	            })
	            .then(function(updatedCart) {
	              // Update cart summary and VAT pricing in-place instead of reloading page
	              if (updatedCart) {
	                refreshCartAfterTaxExemptToggle(updatedCart, enableExempt);
	              }
	            });
	        });
	      })
	      .catch(function(error) {
	        // eslint-disable-next-line no-console
	        console.error('Error while updating cart for tax exemption', error);
	      });
	  }

		  /**
		   * Get the base product handle (without VAT-exempt suffix) for matching
		   */
		  function getBaseProductHandle(handle) {
		    if (!handle) return '';
		    return handle.replace(/-vat-exempt$/, '');
		  }

		  /**
		   * Get variant options as a pipe-separated string for comparison
		   */
		  function getVariantOptionsString(item) {
		    if (!item) return '';
		    var options = getCartItemOptions(item);
		    return options.join('|');
		  }

		  function syncCartLineItemPriceDataAttributes(cart) {
		    try {
		      if (!cart || typeof window.jQuery === 'undefined') {
		        return;
		      }

		      var $ = window.jQuery;

		      if (!Array.isArray(cart.items) || !cart.items.length) {
		        return;
		      }

		      // Cache all cart line rows once so we can fall back to index-based matching.
		      // Limit to the main cart items table(s) to avoid picking up rows from
		      // the totals/shipping tables.
		      var $rows = $('.tt-shopcart-table--js table tbody tr');

		      // Track which rows have been matched to avoid duplicate matches
		      var matchedRowIndices = {};

		      cart.items.forEach(function(item, index) {
		        if (!item) {
		          return;
		        }

		        var $row = null;

		        // First try to locate the row by the quantity input ID (updates_{{ item.id }})
		        if (item.id) {
		          var qtyInput = document.getElementById('updates_' + item.id);
		          if (qtyInput) {
		            $row = $(qtyInput).closest('tr');
		          }
		        }

		        // If variant ID matching fails (due to swapping to/from VAT-exempt products),
		        // match by product handle + variant options instead of just index
		        if (!$row || !$row.length) {
		          var itemBaseHandle = getBaseProductHandle(item.handle);
		          var itemOptionsStr = getVariantOptionsString(item);

		          // Find a row with matching product handle and variant options
		          $rows.each(function(rowIndex) {
		            // Skip already-matched rows
		            if (matchedRowIndices[rowIndex]) {
		              return true; // continue
		            }

		            var $candidate = $(this);
		            var rowHandle = $candidate.attr('data-product-handle') || '';
		            var rowOptions = $candidate.attr('data-variant-options') || '';

		            // Match by base product handle and variant options
		            if (rowHandle === itemBaseHandle && rowOptions === itemOptionsStr) {
		              $row = $candidate;
		              matchedRowIndices[rowIndex] = true;
		              return false; // break
		            }
		          });
		        }

		        // Final fallback: match by index if we still haven't found a row
		        // This handles cases where data attributes aren't present (e.g., cached pages)
		        if ((!$row || !$row.length) && $rows && $rows.length > index && !matchedRowIndices[index]) {
		          $row = $rows.eq(index);
		          matchedRowIndices[index] = true;
		        }

		        if (!$row || !$row.length) {
		          return;
		        }

		        // There can be multiple subtotal price blocks per row (e.g. one for
		        // desktop and one for mobile), so update all of them.
		        var $lineSubtotals = $row.find('.item__subtotal-price .tt-price.subtotal.js-vat-price');
		        if ($lineSubtotals.length) {
		          $lineSubtotals.each(function() {
		            var $el = $(this);
		            $el.attr('data-net-price', item.final_line_price);
		            if (typeof item.original_line_price !== 'undefined') {
		              $el.attr('data-net-compare-price', item.original_line_price);
		            }
		          });
		        }

		        // Likewise, ensure any unit-price blocks for the row are updated.
		        var $lineUnitPrices = $row.find('.item__original-price .tt-price.js-vat-price');
		        if ($lineUnitPrices.length) {
		          $lineUnitPrices.each(function() {
		            var $el = $(this);
		            $el.attr('data-net-price', item.final_price);
		            if (typeof item.original_price !== 'undefined') {
		              $el.attr('data-net-compare-price', item.original_price);
		            }
		          });
		        }
		      });
		    } catch (e) {
		      // eslint-disable-next-line no-console
		      console.error('Error syncing cart line-item price data attributes', e);
		    }
		  }

	  /**
	   * Sync mini cart price data attributes after cart update
	   */
	  function syncMiniCartPriceDataAttributes(cart) {
	    try {
	      if (!cart || typeof window.jQuery === 'undefined') {
	        return;
	      }

	      var $ = window.jQuery;

	      // Update mini cart total price
	      var $miniCartTotal = $('header .tt-cart .tt-cart-total-price.js-vat-price');
	      if ($miniCartTotal.length) {
	        $miniCartTotal.attr('data-net-price', cart.total_price);
	        // Update the displayed price text
	        var totalFormatted = Shopify.formatMoney(cart.total_price, money_format);
	        var $totalSpan = $miniCartTotal.find('.new-price');
	        if ($totalSpan.length) {
	          $totalSpan.html(totalFormatted);
	        } else {
	          $miniCartTotal.html('<span class="new-price">' + totalFormatted + '</span>');
	        }
	      }

	      // Update individual mini cart item prices and links
	      if (Array.isArray(cart.items)) {
	        var $miniCartItems = $('header .tt-cart .tt-cart-list .tt-item');

	        cart.items.forEach(function(item, index) {
	          if (!item) return;

	          // Find the corresponding mini cart item (they should be in order)
	          var $miniItem = $miniCartItems.eq(index);
	          if (!$miniItem.length) return;

	          var $priceEl = $miniItem.find('.tt-price.js-vat-price');
	          if ($priceEl.length) {
	            $priceEl.attr('data-net-price', item.line_price);
	            // Update displayed price
	            var priceFormatted = Shopify.formatMoney(item.line_price, money_format);
	            var $priceSpan = $priceEl.find('.new-price');
	            if ($priceSpan.length) {
	              $priceSpan.html(priceFormatted);
	            } else {
	              $priceEl.html('<span class="new-price">' + priceFormatted + '</span>');
	            }
	          }

	          // Update product link to always point to non-VAT-exempt product
	          var $link = $miniItem.find('a.mini-cart-item-link');
	          if ($link.length && item.url) {
	            var cleanUrl = item.url.replace(/-vat-exempt/g, '');
	            $link.attr('href', cleanUrl);
	          }

	          // Update product title to remove VAT Exempt suffix
	          var $title = $miniItem.find('.tt-title');
	          if ($title.length && item.product_title) {
	            var cleanTitle = item.product_title.replace(/ - VAT Exempt/g, '');
	            $title.text(cleanTitle);
	          }

	          // Update data attributes
	          if (item.handle) {
	            var cleanHandle = item.handle.replace(/-vat-exempt/g, '');
	            $miniItem.attr('data-product-handle', cleanHandle);
	          }
	          if (item.variant_id) {
	            $miniItem.attr('data-variant-id', item.variant_id);
	          }
	        });
	      }

	      // Update mini cart badge count
	      var $badge = $('header .tt-cart .tt-badge-cart');
	      if ($badge.length && typeof cart.item_count !== 'undefined') {
	        $badge.text(cart.item_count);
	        if (cart.item_count === 0) {
	          $badge.addClass('hide');
	        } else {
	          $badge.removeClass('hide');
	        }
	      }

	      // Apply VAT pricing to mini cart prices based on current VAT setting
	      if (typeof window.applyVatToPrices === 'function') {
	        window.applyVatToPrices();
	      }
	    } catch (e) {
	      // eslint-disable-next-line no-console
	      console.error('Error syncing mini cart price data attributes', e);
	    }
	  }


	  function refreshCartAfterTaxExemptToggle(cart, enableExempt) {
	    try {
	      if (!cart || typeof window.jQuery === 'undefined' || typeof Shopify === 'undefined' || typeof Shopify.formatMoney !== 'function') {
	        return;
	      }

	      var $ = window.jQuery;
		      syncCartLineItemPriceDataAttributes(cart);

	      // Also update mini cart prices
	      syncMiniCartPriceDataAttributes(cart);

	      var m = Shopify.formatMoney(cart.total_price, money_format);

	      if ($('#grandtotal').length) {
	        $('#grandtotal').attr('data-total-price', cart.total_price);
	        if ($('#grandtotal .money').length) {
	          $('#grandtotal .money').replaceWith(m);
	        } else {
	          $('#grandtotal td').html(m);
	        }
	      }

	      $('#wrapper-response').length && $('#wrapper-response').hide();
	      $('#tax').length && $('#tax').hide();

	      // Ensure VAT pricing script uses the latest base prices
	      var $grandPrice = $('#grandtotal .tt-price.js-vat-price');
	      if ($grandPrice.length) {
	        $grandPrice.attr('data-net-price', cart.total_price);
	      }

	      // Adjust VAT rate on cart prices based on exemption state
	      if (typeof window.__bentleyCartDefaultVatRate !== 'undefined' && window.__bentleyCartDefaultVatRate !== null) {
	        var newRate = enableExempt ? 0 : window.__bentleyCartDefaultVatRate;
	        $('.js-vat-price[data-vat-rate]').attr('data-vat-rate', String(newRate));
	      }

	      // Apply VAT prices after updating data attributes
	      if (typeof window.applyVatToPrices === 'function') {
	        var vatSetting = enableExempt ? 'ex' : 'inc';
	        window.applyVatToPrices(vatSetting);
	      }

	      $('body').trigger('refreshCurrency');
	    } catch (e) {
	      // eslint-disable-next-line no-console
	      console.error('Error refreshing cart totals after tax exemption toggle', e);
	    }
	  }


  /**
   * Initialize tax exempt dropdown
   */
  function initTaxExemptDropdown() {
    const taxExemptSelect = document.getElementById('tax-exempt-select');
    const exemptionReasonInput = document.getElementById('exemption-reason-input');
    const checkoutButton = document.querySelector('button[name="checkout"]');
    const termsCheckbox = document.getElementById('cart-term-conditions-checkbox');

    if (!taxExemptSelect || !checkoutButton) {
      return;
    }

    // Store default VAT rate (used when toggling tax exemption off again)
    // Use the theme setting DEFAULT_VAT_RATE or detect from data attribute
    if (typeof window.__bentleyCartDefaultVatRate === 'undefined') {
      try {
        var defaultRate = null;
        // First try to get from window.DEFAULT_VAT_RATE (set in theme.liquid)
        if (typeof window.DEFAULT_VAT_RATE !== 'undefined' && window.DEFAULT_VAT_RATE > 0) {
          defaultRate = window.DEFAULT_VAT_RATE;
        } else {
          // Fall back to detecting from a price element (if it has a non-zero rate)
          var vatEl = document.querySelector('.js-vat-price[data-vat-rate]');
          if (vatEl) {
            var parsed = parseFloat(vatEl.getAttribute('data-vat-rate'));
            if (!isNaN(parsed) && parsed > 0) {
              defaultRate = parsed;
            }
          }
        }
        // Default to 20 if we couldn't detect
        window.__bentleyCartDefaultVatRate = defaultRate !== null ? defaultRate : 20;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error detecting default VAT rate for cart', e);
        window.__bentleyCartDefaultVatRate = 20;
      }
    }

    function updateCheckoutDisabledState() {
      if (!checkoutButton) {
        return;
      }

      const taxExemptValue = taxExemptSelect.value;
      const reason = (exemptionReasonInput && exemptionReasonInput.value || '').trim();
      const reasonValidationRequired = taxExemptValue === 'yes';
      const reasonMissing = !reason;
      const shouldDisableForReason = reasonValidationRequired && reasonMissing;

      // Update red border on exemption reason input to highlight it's required
      if (exemptionReasonInput) {
        if (shouldDisableForReason) {
          exemptionReasonInput.style.borderColor = '#cc0000';
          exemptionReasonInput.style.borderWidth = '2px';
        } else {
          exemptionReasonInput.style.borderColor = '';
          exemptionReasonInput.style.borderWidth = '';
        }
      }

      // If tax exempt is yes and no reason provided, force disable checkout
      if (shouldDisableForReason) {
        checkoutButton.disabled = true;
        checkoutButton.dataset.reasonDisabled = 'true';
        return;
      }

      // Reason is either not required or present: clear our flag and, if
      // allowed by the terms checkbox, remove our disabled state.
      if (checkoutButton.dataset.reasonDisabled) {
        delete checkoutButton.dataset.reasonDisabled;
        if (!termsCheckbox || termsCheckbox.checked) {
          checkoutButton.disabled = false;
        }
      }
    }

    // Load saved values from local storage
    let savedTaxExempt = '';
    let savedExemptionReason = '';
    let savedVat = 'ex';

    try {
      if (window.localStorage) {
        savedTaxExempt = localStorage.getItem(TAX_EXEMPT_STORAGE_KEY) || '';
        savedExemptionReason = localStorage.getItem(EXEMPTION_REASON_STORAGE_KEY) || '';
        savedVat = localStorage.getItem(VAT_STORAGE_KEY) || 'ex';
      }
    } catch (e) {
      // Ignore storage errors
    }

    // Determine the effective tax exempt selection, preferring the VAT setting
    let effectiveTaxExempt = savedVat === 'ex' ? 'yes' : 'no';

    if (savedTaxExempt === 'yes' || savedTaxExempt === 'no') {
      const expectedFromVat = savedVat === 'ex' ? 'yes' : 'no';
      effectiveTaxExempt = expectedFromVat;
      try {
        if (window.localStorage && effectiveTaxExempt !== savedTaxExempt) {
          localStorage.setItem(TAX_EXEMPT_STORAGE_KEY, effectiveTaxExempt);
        }
      } catch (err) {
        // Ignore storage errors
      }
    } else {
      try {
        if (window.localStorage) {
          localStorage.setItem(TAX_EXEMPT_STORAGE_KEY, effectiveTaxExempt);
        }
      } catch (err2) {
        // Ignore storage errors
      }
    }

    taxExemptSelect.value = effectiveTaxExempt;

    if (savedExemptionReason && exemptionReasonInput && effectiveTaxExempt === 'yes') {
      exemptionReasonInput.value = savedExemptionReason;
    }

    // Show/hide exemption reason field based on tax exempt selection
    updateExemptionReasonVisibility(taxExemptSelect.value);

    // Apply initial checkout button disabled state
    updateCheckoutDisabledState();

    // Apply initial VAT pricing based on tax exempt selection
    var initialEnableExempt = effectiveTaxExempt === 'yes';
    if (typeof window.__bentleyCartDefaultVatRate !== 'undefined' && window.__bentleyCartDefaultVatRate !== null) {
      var initialVatRate = initialEnableExempt ? 0 : window.__bentleyCartDefaultVatRate;
      var $ = window.jQuery;
      if ($) {
        $('.js-vat-price[data-vat-rate]').attr('data-vat-rate', String(initialVatRate));
      }
    }
    // Apply VAT prices on load
    if (typeof window.applyVatToPrices === 'function') {
      var initialVatSetting = initialEnableExempt ? 'ex' : 'inc';
      window.applyVatToPrices(initialVatSetting);
    }

    // Handle tax exempt change
    taxExemptSelect.addEventListener('change', function() {
      const taxExemptValue = this.value;

      // Save to local storage
      localStorage.setItem(TAX_EXEMPT_STORAGE_KEY, taxExemptValue);

      // Update visibility
      updateExemptionReasonVisibility(taxExemptValue);

      // Clear exemption reason if "No" is selected
      if (taxExemptValue !== 'yes' && exemptionReasonInput) {
        exemptionReasonInput.value = '';
        localStorage.removeItem(EXEMPTION_REASON_STORAGE_KEY);
      }

      // Re-evaluate checkout disabled state based on new selection/reason
      updateCheckoutDisabledState();

      const enableExempt = taxExemptValue === 'yes';
      const currentReason = enableExempt && exemptionReasonInput ? exemptionReasonInput.value : '';

      // Keep visible VAT toggle/prices aligned with tax exemption and update saved preference
      try {
        if (window.localStorage) {
          var vatValue = enableExempt ? 'ex' : 'inc';
          localStorage.setItem(VAT_STORAGE_KEY, vatValue);

          // Notify VAT scripts so toggles/prices update immediately across the site
          internalVatChange = true;
          var event = new CustomEvent('vatChanged', { detail: { vat: vatValue } });
          document.dispatchEvent(event);
          internalVatChange = false;
        }
      } catch (e) {
        // Ignore storage errors
      }

      swapCartToVatExempt(enableExempt, currentReason);
    });

    // Also watch terms & conditions checkbox so we can combine its state with
    // the exemption reason requirement.
    if (termsCheckbox) {
      termsCheckbox.addEventListener('change', function() {
        updateCheckoutDisabledState();
      });
    }

    // Handle exemption reason change (text input)
    if (exemptionReasonInput) {
      exemptionReasonInput.addEventListener('input', function() {
        const exemptionReasonValue = this.value;

        // Save to local storage
        if (exemptionReasonValue) {
          localStorage.setItem(EXEMPTION_REASON_STORAGE_KEY, exemptionReasonValue);
        } else {
          localStorage.removeItem(EXEMPTION_REASON_STORAGE_KEY);
        }

        // Re-evaluate checkout disabled state whenever the reason changes
        updateCheckoutDisabledState();

        if (taxExemptSelect && taxExemptSelect.value === 'yes' && typeof fetch !== 'undefined') {
          fetch('/cart/update.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              attributes: {
                vat_exempt: 'yes',
                vat_exempt_reason: exemptionReasonValue || ''
              }
            })
          }).catch(function(error) {
            // eslint-disable-next-line no-console
            console.error('Error updating VAT exemption reason', error);
          });
        }
      });
    }

    // Keep tax exempt selection in sync with global VAT toggle on the cart page
    document.addEventListener('vatChanged', function(e) {
      if (!e || !e.detail || !e.detail.vat) {
        return;
      }

      // Ignore events dispatched from the tax exempt dropdown itself
      if (internalVatChange) {
        return;
      }

      var vatSetting = e.detail.vat === 'ex' ? 'yes' : 'no';

      if (taxExemptSelect.value === vatSetting) {
        return;
      }

      taxExemptSelect.value = vatSetting;

      try {
        if (window.localStorage) {
          localStorage.setItem(TAX_EXEMPT_STORAGE_KEY, vatSetting);

          if (vatSetting !== 'yes') {
            localStorage.removeItem(EXEMPTION_REASON_STORAGE_KEY);
          }
        }
      } catch (err) {
        // Ignore storage errors
      }

      updateExemptionReasonVisibility(vatSetting);

      if (vatSetting !== 'yes' && exemptionReasonInput) {
        exemptionReasonInput.value = '';
      }

      updateCheckoutDisabledState();

      var enableExemptFromVat = vatSetting === 'yes';
      var currentReasonFromVat = enableExemptFromVat && exemptionReasonInput ? exemptionReasonInput.value : '';

      swapCartToVatExempt(enableExemptFromVat, currentReasonFromVat);
    });
  }

  /**
   * Update visibility of exemption reason dropdown
   */
  function updateExemptionReasonVisibility(taxExemptValue) {
    const exemptionReasonContainer = document.getElementById('exemption-reason-container');

    if (!exemptionReasonContainer) {
      return;
    }

    if (taxExemptValue === 'yes') {
      exemptionReasonContainer.style.display = 'block';
    } else {
      exemptionReasonContainer.style.display = 'none';
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTaxExemptDropdown);
  } else {
    initTaxExemptDropdown();
  }

  // Expose swapCartToVatExempt globally so vat-dropdown.js can sync cart on any page
  window.swapCartToVatExempt = swapCartToVatExempt;
})();

